<template>
  <TransitionRoot as="template" :show="!!props.open">
    <Dialog as="div" class="relative z-backdrop-reset" @close="close">
      <Backdrop />

      <div class="fixed inset-0 z-backdrop-content">
        <div
          class="flex min-h-full justify-center p-4 text-center items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative rounded-md bg-white transition-all max-w-[90vw] w-full min-h-[max(40vh,600px)] max-h-[90vh] sm:max-h-[70vh] flex flex-col"
              :class="
                steps?.length
                  ? 'sm:max-w-[min(48rem,90vw)]'
                  : 'sm:max-w-[min(32rem,90vw)]'
              "
            >
              <button
                type="button"
                class="rounded-sm bg-white text-gray-500 hover:text-gray-400 focus:outline-none absolute top-2 right-4 p-2.5"
                @click="close"
              >
                <span class="sr-only">Close</span>
                <XMarkIcon class="h-6 w-6" aria-hidden="true" />
              </button>
              <div
                class="bg-white text-left px-8 py-4 pr-12 rounded-t-lg border-b border-gray-200"
              >
                <p class="text-gray-900 text-lg font-semibold">{{ title }}</p>
                <p class="text-gray-600 text-sm">{{ description }}</p>
              </div>

              <div
                class="flex flex-col md:flex-row items-stretch flex-grow overflow-hidden"
              >
                <!-- Steps -->
                <div
                  v-if="steps?.length"
                  class="hidden sm:block bg-gray-50 p-6 rounded-bl-lg border-r border-gray-200"
                >
                  <div
                    v-for="(step, index) in steps"
                    :key="'creation_step_' + step.key"
                    class="flex gap-3 max-w-[240px]"
                  >
                    <div class="flex flex-col items-center">
                      <StepIndicationIcon
                        v-if="currentStepIndex <= index"
                        class="w-6 h-6 ring-4 rounded-full ring-transparent z-10 text-gray-200"
                        :class="{
                          '!text-brand-900 !ring-gray-200':
                            currentStep.key === step.key,
                        }"
                      />
                      <div class="rounded-full bg-brand-900" v-else>
                        <StepCompletedIcon class="w-6 h-6 text-white" />
                      </div>
                      <div
                        v-if="index + 1 < steps.length"
                        class="w-0.5 grow bg-gray-100 rounded-xs my-1"
                        :class="{
                          '!bg-brand-900': currentStepIndex > index,
                        }"
                      />
                    </div>
                    <div class="grow mb-6 text-left">
                      <p class="text-sm font-semibold text-gray-900">
                        {{ step.label }}
                      </p>
                      <p class="text-sm text-gray-900">
                        {{ step.description }}
                      </p>
                    </div>
                  </div>
                </div>
                <!-- Content -->
                <div class="flex-grow flex flex-col">
                  <div
                    class="p-6 text-left flex flex-col gap-4 flex-grow basis-0 md:basis-full overflow-scroll"
                  >
                    <DynamicInputComponent
                      v-for="(field, index) in currentStep.fields"
                      :key="currentStep.key + '_' + field.key"
                      :modelValue="form[field.key]"
                      @update:modelValue="onInput"
                      :field="field"
                      :form="form"
                      class="!p-0 !m-0"
                      :debounce="false"
                      :autofocus="index === 0"
                    />
                  </div>

                  <div class="p-4 border-t">
                    <Errors />
                  </div>

                  <div
                    class="flex justify-between p-6 border-t border-gray-300"
                  >
                    <div>
                      <button
                        v-if="allowPreviousStep"
                        type="button"
                        class="inline-flex gap-2 w-auto justify-center rounded-md border border-gray-300 bg-gray-200 px-3.5 py-2 text-base font-medium text-brand-900 shadow-sm hover:bg-gray-200/80 focus:bg-gray-200/80 focus:outline-gray-900 sm:text-sm"
                        @click="previousStep"
                      >
                        <ArrowNarrowLeft class="w-5 h-5 text-brand-900" />
                        <span>
                          {{ t("components.buttons.go_back") }}
                        </span>
                      </button>
                    </div>

                    <button
                      v-if="!steps?.length || isLastStep"
                      :disabled="isLoading"
                      type="button"
                      class="inline-flex w-auto justify-center rounded-md border border-transparent bg-brand-800 px-3.5 py-2 text-base font-medium text-white shadow-sm hover:bg-brand-800/80 focus:bg-brand-800/80 focus:outline-brand-900 sm:text-sm disabled:bg-gray-200 disabled:!cursor-default disabled:pointer-events-none"
                      @click="submit"
                    >
                      {{
                        confirmationLabel ??
                        t("admin.settings.confirmation.confirm")
                      }}
                    </button>
                    <button
                      v-else
                      :disabled="!allowNextStep"
                      type="button"
                      class="inline-flex gap-2 w-auto justify-center rounded-md border border-transparent bg-brand-800 px-3.5 py-2 text-base font-medium text-white shadow-sm hover:bg-brand-800/80 focus:bg-brand-800/80 focus:outline-brand-900 sm:text-sm disabled:bg-gray-200 disabled:!cursor-default disabled:pointer-events-none"
                      @click="nextStep"
                    >
                      <span>
                        {{ t("components.buttons.next_step") }}
                      </span>
                      <ArrowNarrowRight class="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, watch, onMounted } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { useI18n } from "vue-i18n";
import Backdrop from "@/Tailwind/Components/Backdrop.vue";
import DynamicInputComponent from "@/Tailwind/Components/Settings/DynamicInputComponent.vue";
import { useForm } from "@inertiajs/vue3";
import StepIndicationIcon from "@/Tailwind/Icon/StepIndicationIcon.vue";
import StepCompletedIcon from "@/Tailwind/Icon/StepCompletedIcon.vue";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import ArrowNarrowRight from "@/Tailwind/Icon/ArrowNarrowRight.vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { useFormSetup } from "@/Tailwind/Mixins/useFormSetup";
import Errors from "@/Tailwind/Components/Errors.vue";

const emit = defineEmits(["submit", "close"]);
const props = defineProps({
  open: Boolean,
  title: String,
  description: String,
  headerIcon: Object,
  fields: Array,
  steps: Array,
  cancelLabel: String,
  confirmationLabel: String,
  loading: Boolean,
});

const { t } = useI18n();

const { mapDetailsToForm } = useFormSetup();

const submit = () => {
  if (isLoading.value) {
    return;
  }

  emit("submit", form);
};
const close = () => emit("close");

// We'll override this so the keys get set
// When the keys are not set initially they are not reactive
let form = useForm({});

const isLoading = computed(() => {
  return !!props.loading;
});

const creationSteps = computed(() => {
  if (props.steps) {
    return props.steps;
  }

  return [
    {
      key: null,
      fields: props.fields,
    },
  ];
});

const currentStep = ref(creationSteps.value[0]);

const currentStepIndex = computed(() => {
  return creationSteps.value.findIndex(
    (step) => step.key === currentStep.value.key
  );
});

const isLastStep = computed(() => {
  return currentStepIndex.value === creationSteps.value.length - 1;
});

const allowNextStep = computed(() => {
  return currentStep.value.fields.every((field) => {
    const value = form[field.key];
    return field.required
      ? !!(typeof value === "string" ? value.trim() : value)
      : true;
  });
});

const nextStep = () => {
  const nextIndex = currentStepIndex.value + 1;

  if (nextIndex < creationSteps.value.length) {
    currentStep.value = creationSteps.value[nextIndex];
  }
};

const allowPreviousStep = computed(() => {
  return (
    creationSteps.value.findIndex(
      (step) => step.key === currentStep.value.key
    ) > 0
  );
});

const previousStep = () => {
  const previousIndex = currentStepIndex.value - 1;

  if (previousIndex >= 0) {
    currentStep.value = creationSteps.value[previousIndex];
  }
};

watch(
  () => props.open,
  () => {
    if (props.open) {
      currentStep.value = creationSteps.value[0];
      form = mapDetailsToForm(props.fields, null, props.steps);
    }
  }
);

onMounted(() => {
  // If the settings panel is mounted in open state, it will not trigger the watcher
  if (props.open) {
    currentStep.value = creationSteps.value[0];
    form = mapDetailsToForm(props.fields, null, props.steps);
  }
});

// We can't use vModel because the dynamic input component will
// emit different fields if it is a composite field
const onInput = (field, value) => {
  form[field.key] = value;
};
</script>
